import Joi from 'joi';
import { TCmsBlocks } from '@features/cms/typescript';
import logger from '@utils/logger';
import visualBlockSchema from '@utils/validate/blocksSchemes/visualBlockSchema';
import videoBlockSchema from '@utils/validate/blocksSchemes/videoBlockSchema';
import designersBlockSchema from '@utils/validate/blocksSchemes/designersBlockSchema';
import headerSectionSchema from '@utils/validate/blocksSchemes/headerSectionSchema';
import visualWithButtonBlockSchema from '@utils/validate/blocksSchemes/visualWithButtonBlockSchema';
import featuresBlockSchema from '@utils/validate/blocksSchemes/featuresBlockSchema';
import spacingBlockSchema from '@utils/validate/blocksSchemes/spacingBlockSchema';
import productPremiumBlockSchema from '@utils/validate/blocksSchemes/productPremiumBlockSchema';
import wysiwygSchema from '@utils/validate/blocksSchemes/wysiwygSchema';
import productGalleryBlockSchema from '@utils/validate/blocksSchemes/productGalleryBlockSchema';
import descriptionWithSlidesBlockSchema from '@utils/validate/blocksSchemes/descriptionWithSlidesBlockSchema';
import pageHeaderBlockScheam from '@utils/validate/blocksSchemes/pageHeaderBlockScheam';
import mosaicBlockSchema from '@utils/validate/blocksSchemes/mosaicBlockSchema';
import fullImageWithHeadingSchema from '@utils/validate/blocksSchemes/fullImageWithHeadingSchema';
import smallHeaderWithTextSchema from '@utils/validate/blocksSchemes/smallHeaderWithTextSchema';
import liveChatBlockSchema from '@utils/validate/blocksSchemes/liveChatBlockSchema';
import catalogSectionBlockSchema from '@utils/validate/blocksSchemes/catalogSectionBlockSchema';
import inspirationProductGallerySingleSchema from '@utils/validate/blocksSchemes/inspirationProductGallerySingleSchema';
import inspirationProductGalleryDuoSchema from '@utils/validate/blocksSchemes/inspirationProductGalleryDuoSchema';
import inspirationProductGalleryTrioSchema from '@utils/validate/blocksSchemes/inspirationProductGalleryTrioSchema';
import inspirationProductGalleryQuartetSchema from '@utils/validate/blocksSchemes/inspirationProductGalleryQuartetSchema';
import inspirationProductsSliderSchema from '@utils/validate/blocksSchemes/inspirationProductsSliderSchema';
import inspirationHeaderBlockSchema from '@utils/validate/blocksSchemes/inspirationHeaderBlockSchema';
import servicesSchema from '@utils/validate/blocksSchemes/servicesSchema';
import frameSchema from '@utils/validate/blocksSchemes/frameSchema';
import slidesWithDescriptionBlockSchema from '@utils/validate/blocksSchemes/slidesWithDescriptionBlockSchema';
import slidesWithTextSchema from '@utils/validate/blocksSchemes/slidesWithTextSchema';
import videoSchema from '@utils/validate/blocksSchemes/videoSchema';
import socialMediaSchema from '@utils/validate/blocksSchemes/socialMediaSchema';
import imageSchema from '@utils/validate/blocksSchemes/imageSchema';
import voxboxInfoBlockSchema from '@utils/validate/blocksSchemes/voxboxInfoBlockSchema';
import listingProjectBlockSchema from '@utils/validate/blocksSchemes/listingProjectBlockSchema';
import listingIdeaBlockSchema from '@utils/validate/blocksSchemes/listingIdeaBlockSchema';
import tableSchema from '@utils/validate/blocksSchemes/tableSchema';
import { LOG_TYPE } from '@const/index';
import categoryTilesSchema from '@utils/validate/blocksSchemes/categoryTilesSchema';
import categoryGallerySchema from '@utils/validate/blocksSchemes/categoryGallerySchema';
import categoryHeaderBlockSchema from '@utils/validate/blocksSchemes/categoryHeaderBlockSchema';
import CategoryLogoCarouselSchema from '@utils/validate/blocksSchemes/CategoryLogoCarouselSchema';
import categoryLabelBlockSchema from '@utils/validate/blocksSchemes/categoryLabelBlockSchema';
import CategoryUSPBlockSchema from '@utils/validate/blocksSchemes/CategoryUSPBlockSchema';
import categorySliderWithNavSchema from '@utils/validate/blocksSchemes/categorySliderWithNavSchema';
import categoryCollectionCarouselSchema from '@utils/validate/blocksSchemes/categoryCollectionCarouselSchema';
import DescriptionWidthImageSchema from '@utils/validate/blocksSchemes/DescriptionWidthImageSchema';
import MultiIconSchema from '@utils/validate/blocksSchemes/MultiIconSchema';
import mainHeaderBlockSchema from '@utils/validate/blocksSchemes/mainHeaderBlockSchema';
import homeCategoryTilesBlockSchema from '@utils/validate/blocksSchemes/homeCategoryTilesBlockSchema';
import offerCardsBlockSchema from '@utils/validate/blocksSchemes/offerCardsBlockSchema';
import productsCarouselBlockSchema from '@utils/validate/blocksSchemes/productsCarouselBlockSchema';
import fullscreenMediaBlockSchema from '@utils/validate/blocksSchemes/fullscreenMediaBlockSchema';
import promoBannerBlockSchema from '@utils/validate/blocksSchemes/promoBannerBlockSchema';
import imageGalleryBlockSchema from '@utils/validate/blocksSchemes/imageGalleryBlockSchema';
import sloganHomeBlockSchema from '@utils/validate/blocksSchemes/sloganHomeBlockSchema';
import offerBlockSchema from '@utils/validate/blocksSchemes/offerBlockSchema';
import newSpacingBlockSchema from '@utils/validate/blocksSchemes/newSpacingBlockSchema';
import storiesBlockSchema from '@utils/validate/blocksSchemes/storiesBlockSchema';
import yourInteriorsBlockSchema from '@utils/validate/blocksSchemes/yourInteriorsBlockSchema';
import titleBlockSchema from '@utils/validate/blocksSchemes/titleBlockSchema';
import ideasBlockSchema from '@utils/validate/blocksSchemes/ideasBlockSchema';
const validateBlockData = (schema: Joi.ObjectSchema<unknown>, block: TCmsBlocks, abortEarly = true): {
  error: boolean;
  message: string | string[];
} => {
  const result = schema.validate(block, {
    abortEarly
  });
  let message: string | string[] = '';
  if (result?.error) {
    if (result?.error?.details && Array.isArray(result.error.details)) {
      message = [];
      result.error.details.forEach(detail => {
        if (Array.isArray(message)) message.push(detail.message);
      });
    } else {
      message = result?.error ? result.error.toString() : '';
    }
    const isServer = typeof window === 'undefined';
    if (isServer) {
      const logObject = {
        type: LOG_TYPE.blockValidationError,
        data: {
          block,
          result
        }
      };
      logger.error({
        ...logObject
      });
    }
    return {
      error: true,
      message
    };
  }
  return {
    error: false,
    message
  };
};
const validateBlock = (block: TCmsBlocks, abortEarly = true): {
  error: boolean;
  message: string | string[];
} => {
  switch (block?.type) {
    case 'visualBlock':
      return validateBlockData(visualBlockSchema, block, abortEarly);
    case 'videosBlock':
      return validateBlockData(videoBlockSchema, block, abortEarly);
    case 'designersBlock':
      return validateBlockData(designersBlockSchema, block, abortEarly);
    case 'visualWithButtonBlock':
      return validateBlockData(visualWithButtonBlockSchema, block, abortEarly);
    case 'featuresBlock':
      return validateBlockData(featuresBlockSchema, block, abortEarly);
    case 'spacingBlock':
      return validateBlockData(spacingBlockSchema, block, abortEarly);
    case 'productPremiumBlock':
      return validateBlockData(productPremiumBlockSchema, block, abortEarly);
    case 'wysiwyg':
      return validateBlockData(wysiwygSchema, block, abortEarly);
    case 'productGalleryBlock':
      return validateBlockData(productGalleryBlockSchema, block, abortEarly);
    case 'descriptionWithSlidesBlock':
      return validateBlockData(descriptionWithSlidesBlockSchema, block, abortEarly);
    case 'pageHeaderBlock':
      return validateBlockData(pageHeaderBlockScheam, block, abortEarly);
    case 'mosaicBlock':
      return validateBlockData(mosaicBlockSchema, block, abortEarly);
    case 'fullImageWithHeading':
      return validateBlockData(fullImageWithHeadingSchema, block, abortEarly);
    case 'smallHeaderWithText':
      return validateBlockData(smallHeaderWithTextSchema, block, abortEarly);
    case 'liveChatBlock':
      return validateBlockData(liveChatBlockSchema, block, abortEarly);
    case 'catalogSectionBlock':
      return validateBlockData(catalogSectionBlockSchema, block, abortEarly);
    case 'inspirationProductGallerySingle':
      return validateBlockData(inspirationProductGallerySingleSchema, block, abortEarly);
    case 'inspirationProductGalleryDuo':
      return validateBlockData(inspirationProductGalleryDuoSchema, block, abortEarly);
    case 'inspirationProductGalleryTrio':
      return validateBlockData(inspirationProductGalleryTrioSchema, block, abortEarly);
    case 'inspirationProductGalleryQuartet':
      return validateBlockData(inspirationProductGalleryQuartetSchema, block, abortEarly);
    case 'inspirationProductsSlider':
      return validateBlockData(inspirationProductsSliderSchema, block, abortEarly);
    case 'inspirationHeaderBlock':
      return validateBlockData(inspirationHeaderBlockSchema, block, abortEarly);
    case 'services':
      return validateBlockData(servicesSchema, block, abortEarly);
    case 'frame':
      return validateBlockData(frameSchema, block, abortEarly);
    case 'header':
      return validateBlockData(headerSectionSchema, block, abortEarly);
    case 'slidesWithDescriptionBlock':
      return validateBlockData(slidesWithDescriptionBlockSchema, block, abortEarly);
    case 'slidesWithText':
      return validateBlockData(slidesWithTextSchema, block, abortEarly);
    case 'video':
      return validateBlockData(videoSchema, block, abortEarly);
    case 'socialMedia':
      return validateBlockData(socialMediaSchema, block, abortEarly);
    case 'image':
      return validateBlockData(imageSchema, block, abortEarly);
    case 'voxboxInfoBlock':
      return validateBlockData(voxboxInfoBlockSchema, block, abortEarly);
    case 'listingProjectBlock':
      return validateBlockData(listingProjectBlockSchema, block, abortEarly);
    case 'listingIdeaBlock':
      return validateBlockData(listingIdeaBlockSchema, block, abortEarly);
    case 'table':
      return validateBlockData(tableSchema, block, abortEarly);
    case 'categoryTiles':
      return validateBlockData(categoryTilesSchema, block, abortEarly);
    case 'categoryGallery':
      return validateBlockData(categoryGallerySchema, block, abortEarly);
    case 'categoryHeader':
      return validateBlockData(categoryHeaderBlockSchema, block, abortEarly);
    case 'labelBlock':
      return validateBlockData(categoryLabelBlockSchema, block, abortEarly);
    case 'categorySliderWithNav':
      return validateBlockData(categorySliderWithNavSchema, block, abortEarly);
    case 'categoryLogoCarousel':
      return validateBlockData(CategoryLogoCarouselSchema, block, abortEarly);
    case 'categoryCollectionCarousel':
      return validateBlockData(categoryCollectionCarouselSchema, block, abortEarly);
    case 'categoryUSPBlock':
      return validateBlockData(CategoryUSPBlockSchema, block, abortEarly);
    case 'multiIcon':
      return validateBlockData(MultiIconSchema, block, abortEarly);
    case 'descriptionWidthImage':
      return validateBlockData(DescriptionWidthImageSchema, block, abortEarly);
    case 'showroomAdvantages':
      return {
        error: false,
        message: ''
      };
    case 'accordion':
      return {
        error: false,
        message: ''
      };
    case 'mainHeader':
      return validateBlockData(mainHeaderBlockSchema, block, abortEarly);
    case 'homeCategoryTiles':
      return validateBlockData(homeCategoryTilesBlockSchema, block, abortEarly);
    case 'offerCardsBlock':
      return validateBlockData(offerCardsBlockSchema, block, abortEarly);
    case 'productsCarouselBlock':
      return validateBlockData(productsCarouselBlockSchema, block, abortEarly);
    case 'fullscreenMediaBlock':
      return validateBlockData(fullscreenMediaBlockSchema, block, abortEarly);
    case 'promoBannerBlock':
      return validateBlockData(promoBannerBlockSchema, block, abortEarly);
    case 'imageGalleryBlock':
      return validateBlockData(imageGalleryBlockSchema, block, abortEarly);
    case 'sloganHomeBlock':
      return validateBlockData(sloganHomeBlockSchema, block, abortEarly);
    case 'offerBlock':
      return validateBlockData(offerBlockSchema, block, abortEarly);
    case 'newsBlock':
      return validateBlockData(offerCardsBlockSchema, block, abortEarly);
    case 'spacingNewBlock':
      return validateBlockData(newSpacingBlockSchema, block, abortEarly);
    case 'ideasBlock':
      return validateBlockData(ideasBlockSchema, block, abortEarly);
    case 'storiesBlock':
      return validateBlockData(storiesBlockSchema, block, abortEarly);
    case 'yourInteriorsBlock':
      return validateBlockData(yourInteriorsBlockSchema, block, abortEarly);
    case 'titleBlock':
      return validateBlockData(titleBlockSchema, block, abortEarly);
    default:
      return {
        error: true,
        message: 'Block type unknown'
      };
  }
};
export default validateBlock;